import { loadExerciseLibrary, loadExerciseMaxes, createExercise } from '../lib/grit-data.js';
import { fmtWeight, repRange } from '../lib/formatters.js';
import { displayName, displayNameHtml } from '../lib/exercise-names.js';
import { buildSwapOptions } from '../lib/swap-options.js';
import { openLiftForm, liftFormDefaults } from './lift-form.js';
import { toast } from './toast.js';

let overlay = null;

// onPick(option, makeDefault, custom): custom holds the typed numbers of a written-in lift.
export async function openSwap(pe, onPick) {
  close();
  const [library, maxes] = await Promise.all([loadExerciseLibrary(), loadExerciseMaxes()]);
  const lift = { ...pe.exercise, id: pe.exercise_id };
  const all = buildSwapOptions(lift, library);
  const closest = pe.exercise.swap_group;
  let scope = 'today';
  let q = '';

  const row = (o) => {
    const max = maxes[o.id];
    return `<button data-id="${o.id}" class="grit-row w-full flex items-center gap-3 bg-pagebg rounded-2xl p-3 text-left">
        <div class="w-9 h-9 rounded-lg bg-grit-soft text-grit text-[12px] font-bold flex items-center justify-center flex-none">${(o.equipment || '?').slice(0, 2).toUpperCase()}</div>
        <div class="flex-1 min-w-0">
          <div class="font-bold text-[15px] text-ink flex items-center gap-1.5 flex-wrap">${displayNameHtml(o)}${closest && o.swap_group === closest ? '<span class="grit-chip-accent">closest</span>' : ''}</div>
          <div class="text-[13px] text-inksoft">${o.muscle_group || ''} · ${o.equipment || ''} · ${o.is_primary ? 'big lift' : 'accessory'}</div>
        </div>
        <div class="text-[12px] font-bold flex-none ${max != null ? 'text-ink' : 'text-inksoft'}">${max != null ? `best: ${fmtWeight(max)}` : 'New'}</div>
      </button>`;
  };
  const matches = (o) => !q || displayName(o).toLowerCase().includes(q) || o.name.toLowerCase().includes(q) || (o.muscle_group || '').includes(q);

  function listHtml() {
    const same = all.same.filter(matches);
    const diff = all.different.filter(matches);
    const near = all.nearby.filter(matches);
    if (!same.length && !diff.length && !near.length) return `<p class="text-inksoft text-[15px] py-4 text-center">${q ? 'Not on the list. Write it in above.' : 'No alternatives for this muscle yet.'}</p>`;
    return `${same.map(row).join('')}
      ${diff.length ? `<div class="grit-eyebrow pt-3 pb-1">Different kind of lift (the book swaps big for big, small for small, p.320)</div>${diff.map(row).join('')}` : ''}
      ${near.length ? `<details class="pt-2" ${q ? 'open' : ''}><summary class="min-h-[44px] flex items-center font-semibold text-[15px] text-grit-press cursor-pointer">Muscles around it (${near.length})</summary><div class="space-y-2 mt-1">${near.map(row).join('')}</div></details>` : ''}`;
  }

  overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 z-[120] flex items-end justify-center bg-black/30';
  overlay.innerHTML = `
    <div class="grit-sheet overflow-auto">
      <div class="grit-sheet-handle"></div>
      <div class="text-[17px] font-bold text-ink">Swap ${displayNameHtml(pe.exercise)}</div>
      <div class="text-[15px] text-inksoft">Same kind of lift keeps today's ${repRange(pe.rep_low, pe.rep_high)} reps</div>
      <div class="grit-seg mt-3" id="swap-scope">
          <button data-scope="today" class="on">Just today</button>
          <button data-scope="default">Make it the default</button>
      </div>
      <button id="swap-write" class="grit-btn-quiet mt-3 !text-grit">Write in a lift</button>
      <input id="swap-q" class="grit-input mt-2" placeholder="Search lifts or muscles" aria-label="Search lifts" />
      <div id="swap-list" class="space-y-2 mt-3">${listHtml()}</div>
      <button data-cancel class="w-full text-inksoft font-bold py-3 mt-2">Cancel</button>
    </div>
`;

  const bindRows = () => overlay.querySelectorAll('[data-id]').forEach((btn) => {
    btn.onclick = () => {
      const opt = library.find((x) => x.id === btn.dataset.id);
      const makeDefault = scope === 'default';
      close();
      onPick(opt, makeDefault);
    };
  });
  overlay.querySelectorAll('[data-scope]').forEach((b) => b.onclick = () => {
    scope = b.dataset.scope;
    overlay.querySelectorAll('[data-scope]').forEach((x) => { x.className = x.dataset.scope === scope ? 'on' : ''; });
  });
  overlay.querySelector('#swap-q').oninput = (e) => {
    q = e.target.value.trim().toLowerCase();
    overlay.querySelector('#swap-list').innerHTML = listHtml();
    bindRows();
  };
  overlay.querySelector('#swap-write').onclick = () => {
    const makeDefault = scope === 'default';
    const name = overlay.querySelector('#swap-q').value.trim();
    close();
    openLiftForm({
      ...liftFormDefaults(pe), name, saveLabel: 'Swap it in',
      onSave: async (v) => {
        const ex = await createExercise({ name: v.name, muscle_group: v.muscle, equipment: v.equipment, tracking: v.tracking, default_rep_low: v.lo, default_rep_high: v.hi, default_increment_lb: v.inc });
        if (!ex) { toast("Couldn't save it, try again", { tone: 'bad' }); return; }
        onPick(ex, makeDefault, v);
      },
    });
  };
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('[data-cancel]').onclick = close;
  bindRows();
  document.body.appendChild(overlay);
}

function close() { if (overlay) { overlay.remove(); overlay = null; } }
